// Copyright (c) 2016-2019 Akim McMath

// Permission is hereby granted, free of charge, to any person obtaining a copy of
// this software and associated documentation files (the "Software"), to deal in
// the Software without restriction, including without limitation the rights to
// use, copy, modify, merge, publish, distribute, sublicense, and/or sell copies
// of the Software, and to permit persons to whom the Software is furnished to do
// so, subject to the following conditions:

// The above copyright notice and this permission notice shall be included in all
// copies or substantial portions of the Software.

// THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
// IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
// FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
// AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
// LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
// OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE
// SOFTWARE.

import { isFunction, isNil, isObject, isArray } from 'lodash';

type NonPrimitive = Record<string, any> | Array<any>;

export type MapFn = (key: string, value: any) => string;

export interface Opts {
  thisArg?: any;
}

export class DeepMapKeys {
  private cache = new WeakMap<NonPrimitive, any>();

  constructor(
    private mapFn: MapFn,
    private opts: Opts
  ) {}

  public map(value: any): any {
    return isArray(value) ? this.mapArray(value) : isObject(value) ? this.mapObject(value) : value;
  }

  private mapArray(arr: any[]): any[] {
    if (this.cache.has(arr)) {
      return this.cache.get(arr);
    }

    const length = arr.length;
    const result: any[] = [];
    this.cache.set(arr, result);

    for (let i = 0; i < length; i++) {
      result.push(this.map(arr[i]));
    }

    return result;
  }

  private mapObject(obj: NonPrimitive): NonPrimitive {
    if (this.cache.has(obj)) {
      return this.cache.get(obj);
    }

    const {
      mapFn,
      opts: { thisArg }
    } = this;
    const result: NonPrimitive = {};
    this.cache.set(obj, result);

    for (const key in obj) {
      if (obj.hasOwnProperty(key)) {
        result[mapFn.call(thisArg, key, obj[key])] = this.map(obj[key]);
      }
    }

    return result;
  }
}

export function deepMapKeys<T>(object: any, mapFn: MapFn, options?: Opts): T {
  options = isNil(options) ? {} : options;

  if (!mapFn) {
    throw new Error('mapFn is required');
  } else if (!isFunction(mapFn)) {
    throw new TypeError('mapFn must be a function');
  } else if (!isObject(options)) {
    throw new TypeError('options must be an object');
  }

  return new DeepMapKeys(mapFn, options).map(object);
}
